import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoChevronDownOutline, IoChevronUpOutline } from 'react-icons/io5';
import {
  flexRender,
  getCoreRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import type { IProduct } from '~/types/products';
import styles from './products-table.module.scss';

const columns = [{
  header: 'Nom',
  accessorKey: 'title',
}, {
  header: 'Dernière mise à jour',
  accessorKey: 'updatedAt',
  cell: ({ row }: any) => {
    const product = row.original as IProduct;
    if (!product.updatedAt) return <div>-</div>;
    return <div>{new Date(product.updatedAt).toLocaleDateString()}</div>;
  },
}];

const ProductsTable = (
  {
    products,
    handleSorting,
    baseTo = '/products',
  }: {
    products: IProduct[];
    handleSorting?: any;
    baseTo?: string;
  },
) => {
  const [sorting, setSorting] = useState<any>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof handleSorting === 'function') handleSorting(sorting[0] || null);
  }, [sorting]);

  const data = useMemo(() => products, [products]);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  });

  const onRowClick = (row: Row<IProduct>) => {
    navigate(`${baseTo}/${row.original._id}`);
  };

  return (
    <div className={styles.productsTable}>
      <table>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <p>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {typeof header.column.getIsSorted() === 'string' ? (
                      {
                        asc: IoChevronUpOutline({}),
                        desc: IoChevronDownOutline({}),
                      }[header.column.getIsSorted() as string]
                    ) : (
                      <span className={styles.sortIcons}>
                        {IoChevronUpOutline({})}
                        {IoChevronDownOutline({})}
                      </span>
                    )}
                  </p>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} onClick={() => onRowClick(row)}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  <div className={`${styles.cell} ${styles[cell.column.id]}`}>
                    <div className={styles.content}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProductsTable;
